<template>
  <div class="bg-gray-900 min-h-screen flex items-center justify-center">
    <div class="fixed top-4 right-4 z-10 hidden">
      <button @click="downloadPng"
        class="rounded-md border border-gray-600 bg-gray-800 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2">
        Download PNG
      </button>
    </div>
    <div ref="captureEl">
      <OGPreview
        :title="dane?.title || ''"
        :city="dane?.city || ''"
        :address="address"
        :description="shortDesc"
        :ratingValue="rating"
        :reviewCount="reviewCount"
        :authorsAvatars="authorsAvatars"
        :externalAvgRating="placeDetails?.fetched_reviews_average_rating"
        :externalCount="placeDetails?.fetched_reviews_count"
        :googleRating="placeDetails?.google_rating"
        :googleCount="placeDetails?.google_review_count"
        :trend="placeDetails?.rating_trend"
      />
    </div>
  </div>
  
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import OGPreview from '@/components/OGPreview.vue'
import { apiFetch } from '@/composables/apiClient'

const route = useRoute()
const dane = ref(null)
const authorsAvatars = ref([])
const shortDesc = ref('')
const address = ref('')
const rating = ref('')
const reviewCount = ref('')
const placeDetails = ref(null)
const captureEl = ref(null)

const normalize = (u) => {
  if (!u) return u
  if (/^https?:\/\//i.test(u)) return u
  // ensure absolute via base
  const base = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '')
  if (!base) return u
  return base + (u.startsWith('/') ? u : `/${u}`)
}

onMounted(async () => {
  ensureFontLinks()
  const id = route.params.id
  try {
    const res = await apiFetch(`/api/dane/${id}`)
    if (res.ok) {
      const j = await res.json()
      dane.value = j.data
      address.value = [j.data?.street, j.data?.city].filter(Boolean).join(', ')
      rating.value = j.data?.ratingValue || j.data?.ratingvalue || ''
      reviewCount.value = j.data?.reviewCount || j.data?.reviewcount || ''
    }
    // fetch influencers for avatars
    const infRes = await apiFetch('/api/influencers')
    if (infRes.ok) {
      const j = await infRes.json()
      const list = j.data || []
      // authors may be stringified array in dane.author
      let authors = []
      try { authors = JSON.parse(dane.value?.author || '[]') } catch {}
      const urls = list
        .filter(inf => authors.includes(inf.name))
        .map(inf => normalize(inf.avatar || inf.avatar_url))
        .filter(Boolean)
        .slice(0, 4)
      authorsAvatars.value = urls
    }
    // fetch reviews to get a short description
    const revRes = await apiFetch(`/api/reviews/${id}`)
    if (revRes.ok) {
      const j = await revRes.json()
      const list = j.data || []
      // jeśli nie mamy analysis_summary, fallback do recenzji
      if (!shortDesc.value) {
        shortDesc.value = (list[0]?.shortdesc || list[0]?.text || '').toString()
      }
    }

    // fetch extra place details via admin backend proxy (secured with X-API-Key server-side)
    // This endpoint expects CID; using GID causes 400 in console.
    const detailsLookupId = dane.value?.cid
    if (detailsLookupId) {
      const detRes = await apiFetch(`/admin/place-details/${encodeURIComponent(detailsLookupId)}`)
      if (detRes.ok) {
        const j = await detRes.json()
        if (!j) {
          return
        }
        placeDetails.value = j
        // prefer external/google values when available
        rating.value = j.google_rating ?? j.fetched_reviews_average_rating ?? rating.value
        reviewCount.value = j.google_review_count ?? j.fetched_reviews_count ?? reviewCount.value
        if (j.analysis_summary) {
          shortDesc.value = j.analysis_summary
        }
      }
    }
  } catch (e) {
    console.error(e)
  }
})

// Runtime loader for html2canvas (no bundle change needed)
const ensureHtml2Canvas = () => new Promise((resolve, reject) => {
  if (window.html2canvas) return resolve(window.html2canvas)
  const script = document.createElement('script')
  script.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js'
  script.async = true
  script.onload = () => resolve(window.html2canvas)
  script.onerror = reject
  document.head.appendChild(script)
})

const sanitizeFilename = (str) => (str || 'og-image')
  .toString()
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9-_\. ]/g, '')
  .trim()
  .replace(/\s+/g, '-')

const ensureFontLinks = () => {
  const head = document.head
  const have = (href) => !!head.querySelector(`link[href="${href}"]`)
  const defs = [
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap&text=ABCDEabcdeĄąĆćĘęŁłŃńÓóŚśŹźŻż★★★★★' },
    { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@600;700;800&display=swap' }
  ]
  defs.forEach(d => {
    if (have(d.href)) return
    const l = document.createElement('link')
    l.rel = d.rel
    l.href = d.href
    if (d.crossOrigin) l.crossOrigin = d.crossOrigin
    head.appendChild(l)
  })
}

const waitForFonts = async () => {
  if (document && document.fonts && document.fonts.ready) {
    try { await document.fonts.ready } catch {}
  }
}

const waitForImages = async (root) => {
  const imgs = Array.from(root.querySelectorAll('img'))
  await Promise.all(imgs.map(img => img.complete ? Promise.resolve() : new Promise(res => { img.onload = img.onerror = () => res() })))
}

const downloadPng = async () => {
  try {
    const h2c = await ensureHtml2Canvas()
    const node = captureEl.value
    if (!node) return
    // Ensure layout fully ready
    await waitForFonts()
    await waitForImages(node)
    // Render at exact logical size, later upscale for sharpness
    const baseCanvas = await h2c(node, {
      backgroundColor: null,
      width: 1200,
      height: 630,
      scale: 1,
      useCORS: true,
      foreignObjectRendering: false,
      logging: false,
      scrollX: 0,
      scrollY: 0
    })
    // Upscale to 2x for crisper output without layout shifts
    const out = document.createElement('canvas')
    out.width = 1200 * 2
    out.height = 630 * 2
    const ctx = out.getContext('2d')
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(baseCanvas, 0, 0, out.width, out.height)
    const dataUrl = out.toDataURL('image/png')
    const a = document.createElement('a')
    const fn = `${sanitizeFilename(dane.value?.title)}-${sanitizeFilename(dane.value?.city)}.png`
    a.href = dataUrl
    a.download = fn
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  } catch (e) {
    console.error('PNG download error:', e)
  }
}
</script>


